/** Minimal report snapshot shape used by Report History merge/diff (matches API list rows). */
export interface ReportHistoryVersion {
  uuid: string;
  studyUuid: string;
  status: 'DRAFT' | 'PRELIMINARY' | 'FINAL';
  createdAt: string;
  updatedAt: string;
  conclusion?: {
    uuid: string;
    value: string;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  signature?: {
    createdAt: string;
    signerFirstName: string;
    signerLastName: string;
    signerUuid: string;
    uuid: string;
    report: string;
  } | null;
  addendum?: {
    createdAt: string;
    updatedAt: string;
    description: string;
    reason: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    uuid: string;
  } | null;
  findings: {
    findingClass: string;
    reportFindingClassValue: string;
    reportFindingClassDescription: string;
    value: string;
    uuid: string;
    index: number;
  }[];
  measurements: {
    canonicalMeasurement: string;
    reportMeasurementClassValue: string;
    reportMeasurementClassDescription: string;
    value: number;
    uuid: string;
  }[];
}

function emptyReportShell(): ReportHistoryVersion {
  return {
    uuid: '',
    studyUuid: '',
    status: 'DRAFT',
    createdAt: '',
    updatedAt: '',
    findings: [],
    measurements: [],
    conclusion: null,
    signature: null,
    addendum: null,
  };
}

/** Overlay sparse addendum-only rows onto inherited worksheet state for history diffing. */
export function mergeReportVersions(
  reports: ReportHistoryVersion[]
): ReportHistoryVersion {
  if (!reports.length) return emptyReportShell();
  return reports.reduce((acc, layer) => {
    const measMap = new Map(
      (acc.measurements ?? []).map((m) => [m.canonicalMeasurement, m])
    );
    (layer.measurements ?? []).forEach((m) => {
      measMap.set(m.canonicalMeasurement, m);
    });

    // Statements are rows, so a layer replaces a class it carries wholesale.
    const layerClasses = new Set(
      (layer.findings ?? []).map((f) => f.findingClass)
    );
    const findings = [
      ...(acc.findings ?? []).filter((f) => !layerClasses.has(f.findingClass)),
      ...(layer.findings ?? []),
    ];

    const hasConclusion = Object.hasOwn(layer, 'conclusion');
    const conclusion = hasConclusion ? layer.conclusion : acc.conclusion;

    return {
      ...layer,
      measurements: Array.from(measMap.values()),
      findings,
      conclusion: conclusion ?? null,
    };
  }, emptyReportShell());
}
